import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { SkillProficiencyLevel } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent, TileStickyHeader } from '~/ui/core/tile/Tile';
import { TileFooter } from '~/ui/core/tile/TileFooter';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';
import { ProficiencyScale } from '~/ui/domain/skill/ProficiencyScale';

import { useShortSkillDescriptionsPreferred } from '../../app/common/hooks/use-short-skill-descriptions-preferred';
import { useSkillDescriptionsExpandable } from '../../app/common/hooks/use-skill-descriptions-expandable';
import { useSkillProficiencyLevelName } from '../../app/common/skills';
import { useSkillDetails } from './api/use-skill-details';
import { useSkillLocales } from './api/use-skill-locales';
import { SkillDetailsLoadingSkeleton } from './SkillDetailsLoadingSkeleton';
import { SkillReferences } from './SkillReferences';
import type { NavigatableSidepanelItem } from './types';
import { useCanSeeSkillReferences } from './use-can-see-skill-references';
import { useItemNavigation } from './use-item-navigation';
import { useSetupLanguageFromLocales } from './use-setup-language-from-locales';

const SkillDetailTile = styled(Tile)({
    position: 'relative',
    zIndex: 1,
});

const ProficiencyLevelTerm = styled.dt`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.m};
`;

const Title = styled.div(({ theme }) => ({
    color: 'var(--color-text-primary)',
    flexShrink: 0,
    fontSize: theme.typography.headline.h4.medium.fontSize,
    fontWeight: theme.typography.headline.h4.medium.fontWeight,
    lineHeight: 'normal',
    minWidth: '100%',
    textAlign: 'left',
    width: 'min-content',
}));

const Dl = styled.dl(({ theme }) => ({
    '& > div': {
        border: '1px solid transparent',
        borderRadius: theme.borderRadius.m,
        marginLeft: `-${theme.spacing.xxl}`,
        marginRight: `-${theme.spacing.xxl}`,
        padding: `${theme.spacing.xxl} ${theme.spacing.xxl} ${theme.spacing['3xl']}`,
    },

    '& > div[data-active="true"]': {
        borderColor: 'var(--color-accent)',
    },

    '& > div > dd': {
        color: 'var(--color-text-muted)',
        margin: 0,
    },

    '& > div > dt': {
        ...theme.typography.body.S.semibold,
    },
}));

const ExpandableDescription: FunctionComponent<{
    readonly description?: string;
    readonly shortDescription?: string | null;
    readonly initiallyExpanded?: boolean;
    readonly isExpandable?: boolean;
}> = ({ description, shortDescription, initiallyExpanded = false, isExpandable = true }) => {
    const translate = useTranslate();
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

    const expand = useCallback((): void => {
        setIsExpanded(true);
    }, []);

    if ((isExpandable && isExpanded) || !is.nonEmptyStringAndNotWhitespace(shortDescription)) {
        return <span className="whitespace-pre-line">{description}</span>;
    }

    if (!isExpandable) {
        return <span className="whitespace-pre-line">{shortDescription}</span>;
    }

    return (
        <>
            <span className="whitespace-pre-line">{shortDescription}</span>{' '}
            <button
                className="cursor-pointer border-none bg-transparent p-0 [font:inherit] text-inherit underline"
                type="button"
                onClick={expand}
            >
                {translate('common.more')}…
            </button>
        </>
    );
};

export const SkillsDetailsPanel: FunctionComponent<{
    readonly id: string;
    readonly actions?: ReactNode;
    readonly availableItems?: readonly NavigatableSidepanelItem[];
    readonly disableArrows?: boolean;
    readonly onClose: () => void;
    readonly onNavigate: (skillId: string) => void;
    readonly onPreferredLanguageChange?: (language: LanguageCodeForJSA) => void;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly proficiency?: SkillProficiencyLevel | null;
}> = ({
    id,
    actions,
    availableItems,
    disableArrows = false,
    onClose,
    onNavigate,
    onPreferredLanguageChange,
    preferredLanguage,
    proficiency,
}) => {
    const translate = useTranslate();

    const { data: locales } = useSkillLocales();

    const { activeLanguage, availableLanguages, setActiveLanguage } = useSetupLanguageFromLocales(
        locales,
        preferredLanguage,
    );

    const { data, isError, isLoading } = useSkillDetails(id, activeLanguage);
    const isShortSkillDescriptionPreferred = useShortSkillDescriptionsPreferred();
    const isSkillDescriptionExpandable = useSkillDescriptionsExpandable();
    const canSeeSkillReferences = useCanSeeSkillReferences();

    const { goToNextItem, goToPreviousItem } = useItemNavigation(availableItems, id, onNavigate);

    const { getProficiencyLevelName } = useSkillProficiencyLevelName();

    const handleActiveLanguageChange = useCallback(
        (language: LanguageCodeForJSA): void => {
            setActiveLanguage(language);
            onPreferredLanguageChange?.(language);
        },
        [onPreferredLanguageChange, setActiveLanguage],
    );

    const header = (
        <TileStickyHeader>
            <SidepanelHeader
                actions={actions}
                activeLanguage={
                    is.undefined(activeLanguage)
                        ? undefined
                        : translate(`common.languages.${activeLanguage}` as TranslationKey)
                }
                disableArrows={disableArrows}
                isGenerating={false}
                isLanguagePickerDisabled={false}
                languages={availableLanguages}
                setActiveLanguage={handleActiveLanguageChange}
                onClose={onClose}
                onNext={goToNextItem}
                onPrevious={goToPreviousItem}
            />
        </TileStickyHeader>
    );

    if (isLoading) {
        return (
            <Tile data-testid="skill-details-loading">
                <ScrollArea scrollFade="vertical" scrollFadeSize="var(--spacing-4xl)">
                    <TileContent>
                        {header}
                        <SkillDetailsLoadingSkeleton />
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    if (isError) {
        return (
            <Tile>
                <TileContent>
                    {header}
                    <div>{translate('pages.job-architecture-v2.skill-details-error')}</div>
                </TileContent>
            </Tile>
        );
    }

    if (is.undefined(data)) {
        return (
            <Tile>
                <TileContent>
                    {header}
                    <div>{translate('pages.job-architecture-v2.skill-details-no-data')}</div>
                </TileContent>
            </Tile>
        );
    }

    return (
        <SkillDetailTile
            data-testid="skill-details"
            className="[&_[data-radix-scroll-area-viewport]>div]:block! [&_[data-radix-scroll-area-viewport]>div]:h-full"
        >
            <ScrollArea scrollFade="vertical" scrollFadeSize="var(--spacing-4xl)">
                <TileContent className="flex min-h-full w-full items-start">
                    {header}
                    <div className="flex-1">
                        <Title>{data.title}</Title>
                        <p>
                            <ExpandableDescription
                                key={id}
                                description={data.description}
                                isExpandable={isSkillDescriptionExpandable}
                                shortDescription={isShortSkillDescriptionPreferred ? data.shortDescription : undefined}
                            />
                        </p>
                        <Dl>
                            {data.proficiencyLevelDetails?.map((detail) => {
                                const isActive = proficiency === detail.proficiencyLevel;

                                return (
                                    <div key={detail.proficiencyLevel} data-active={isActive ? 'true' : undefined}>
                                        <ProficiencyLevelTerm>
                                            <ProficiencyScale
                                                active={isActive}
                                                level={detail.proficiencyLevel}
                                                scaleCount={5}
                                            />
                                            {getProficiencyLevelName({
                                                level: detail.proficiencyLevel,
                                                skillType: data.skillType,
                                            })}
                                        </ProficiencyLevelTerm>
                                        <dd>
                                            <ExpandableDescription
                                                key={`${id}-${detail.proficiencyLevel}`}
                                                description={detail.description}
                                                initiallyExpanded={isActive}
                                                isExpandable={isSkillDescriptionExpandable}
                                                shortDescription={
                                                    isShortSkillDescriptionPreferred
                                                        ? detail.shortDescription
                                                        : undefined
                                                }
                                            />
                                        </dd>
                                    </div>
                                );
                            })}
                        </Dl>
                        {canSeeSkillReferences && (
                            <SkillReferences className="mt-xl" language={activeLanguage} skillId={id} />
                        )}
                    </div>
                    <TileFooter />
                </TileContent>
            </ScrollArea>
        </SkillDetailTile>
    );
};
